import { useCallback, useState } from "react";
import { useRouter } from "next/router";

import {
  HiOutlineExclamation,
  HiOutlineExclamationCircle,
} from "react-icons/hi";

import useData from "../src/useData";
import useEnv from "../src/useEnv";
import useTenant from "../src/useTenant";

import { MAX_ENVIRONMENTS } from "../src/constants";
import { EnvData } from "../src/types";

import Button from "./Button";
import Modal from "./Modal";
import ProductionOnlySettings from "./ProductionOnlySettings";
import { useEnvironmentAction, useTenantAction } from "../src/useAction";

interface EnvForkTimeRange {
  pub_id: string;
  min_fork_at_time: string;
  last_rollback_at: string | null;
}
interface Recovery {
  pub_id: string;
  fork_at_time: string;
  created_from_environment_name: string;
  recovered_to_environment_name: string;
  recovered_to_environment_at: string;
  in_place: boolean;
}

// Datetime manipulation functions
const formatLocale = (date: Date): string => {
  return (
    new Intl.DateTimeFormat("default", {
      timeZone: "UTC",
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date) + " UTC"
  );
};
const toDatepickerTime = (date: Date | null | undefined): string => {
  // The datetime-local date/time picker uses strings that are *almost*
  // ISO 8601 timestamps, but without timezone information.
  return date ? truncate(date).toISOString().replace("Z", "") : "";
};
const truncate = (date: Date): Date => {
  const newDate = new Date(date.toISOString());
  newDate.setMilliseconds(0);
  return newDate;
};

const RecoveryManagementView: React.FC<{}> = ({}) => {
  const { tenant } = useTenant();

  const [showForm, setShowForm] = useState<boolean>(false);

  const [envToCopy, setEnvToCopy] = useState<EnvData | null>(null);
  const [datetime, setDatetime] = useState<Date | null>(null);
  const [copyInPlaceModalOpen, setCopyInPlaceModalOpen] =
    useState<boolean>(false);
  const [rollbackModalOpen, setRollbackModalOpen] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { data: forkTimeRange } = useData<EnvForkTimeRange>(() =>
    envToCopy ? `/web/environment/${envToCopy.pub_id}/fork_time_range` : null
  );
  const { data: recoveriesObj } = useData<{ recoveries: Recovery[] }>(() =>
    tenant ? `/web/tenants/${tenant.id}/recent_recoveries` : null
  );
  const recoveries = recoveriesObj?.recoveries || [];

  const createEnvironmentAction = useTenantAction("create_environment");
  const updateEnvironmentAction = useEnvironmentAction(
    "update_environment",
    envToCopy || undefined
  );

  if (tenant === null) {
    return <div></div>;
  }

  const minDatetime = forkTimeRange
    ? truncate(new Date(forkTimeRange.min_fork_at_time))
    : null;
  if (minDatetime) {
    // Because we're truncating the seconds of the minimum date, let's round
    // up to the next minute, so that nobody can accidentally try to restore
    // to before the environment was created. (Unlikely, but who knows.)
    minDatetime.setMinutes(minDatetime.getMinutes() + 1);
  }
  const lastRollbackTime = forkTimeRange?.last_rollback_at
    ? truncate(new Date(forkTimeRange.last_rollback_at))
    : null;

  const atEnvLimit = tenant.environments.length >= MAX_ENVIRONMENTS;

  const datetimeSelectionDisabled = !forkTimeRange;

  const datetimeTooEarly = datetime && minDatetime && datetime < minDatetime;
  const datetimeTooLate = datetime && datetime > new Date();

  const datetimeValid = !datetimeTooLate && !datetimeTooEarly;
  const inputsValid = envToCopy && datetime && datetimeValid;

  return (
    <div>
      <h2 className="text-2xl font-space mb-2">Recovery</h2>
      <div className="text-sm mb-6">
        This tool recovers the facts and policy of an environment from a given
        point in time. You can use this to rollback an environment to a previous
        state, or to clone an existing environment into a new one.
      </div>
      <ProductionOnlySettings
        youCannot="make recovery copies of environments in"
        youWillBeAbleTo="make point-in-time copies of your environments"
        utmContent="backups"
      >
        <h2 className="text-lg">Recent recoveries</h2>
        <div className="mt-2" />
        <table className="table-auto w-full">
          <thead>
            <tr className="border-b text-sm text-gray-600 pb-1">
              <td>Initiated at</td>
              <td>Environment</td>
              <td>Recovered from time</td>
              <td>Recovered from environment</td>
            </tr>
          </thead>
          <tbody>
            {recoveries.map((recovery: Recovery) => (
              <tr key={recovery.pub_id} className="hover:bg-gray-50">
                <td>
                  {formatLocale(new Date(recovery.recovered_to_environment_at))}
                </td>
                <td>
                  <strong>{recovery.recovered_to_environment_name}</strong>
                </td>
                <td>{formatLocale(new Date(recovery.fork_at_time))}</td>
                <td>
                  <strong>{recovery.created_from_environment_name}</strong>
                  {recovery.in_place && " (in-place)"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {recoveries.length == 0 && (
          <div>This organization doesn{"'"}t have any recent recoveries.</div>
        )}
        <div className="mt-6" />

        {!showForm && (
          <Button
            theme="primary"
            medium
            onClick={() => {
              setShowForm(true);
            }}
          >
            Create a recovery &rarr;
          </Button>
        )}

        {showForm && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <div className="mt-4" />
            <div className="text-lg">Create a recovery</div>
            <div className="mt-2" />
            {atEnvLimit && (
              <div className="border rounded px-3 py-2 text-sm mb-6">
                <HiOutlineExclamationCircle className="inline mr-2" />
                <span>
                  Your organization is currently at its environment limit (
                  {MAX_ENVIRONMENTS}), so you can{"'"}t recover into a new
                  environment. Please contact{" "}
                  <a href="mailto:engineering@osohq.com">
                    engineering@osohq.com
                  </a>{" "}
                  to request additional environments. (You can still rollback in
                  place.)
                </span>
              </div>
            )}
            {error && (
              <>
                <div className="border border-red-600 rounded px-3 py-2 text-red-600 text-sm">
                  <HiOutlineExclamation className="inline mr-2" />
                  <span>{error}</span>
                </div>
                <div className="pt-4" />
              </>
            )}
            <fieldset>
              <div>
                <label htmlFor="env-input">Environment to recover:</label>
                <div>
                  <select
                    id="env-input"
                    className="px-2 py-1 text-base min-w-[300px] rounded"
                    value={envToCopy ? envToCopy.pub_id : ""}
                    onChange={(e) => {
                      const pub_id = e.target.value;
                      setEnvToCopy(
                        tenant.environments.find(
                          (env) => env.pub_id === pub_id
                        ) || null
                      );
                    }}
                  >
                    <option value=""></option>
                    {tenant.environments.map((e, idx) => (
                      <option key={idx} value={e.pub_id}>
                        {e.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="pt-4" />
              <div>
                <label htmlFor="datetime-input">Recover from time:</label>
                <div className="flex flex-row items-center space-x-2">
                  <input
                    id="datetime-input"
                    type="datetime-local"
                    className={
                      "border px-2 py-1 text-base min-w-[300px] rounded invalid:border-red-500 " +
                      (datetimeSelectionDisabled
                        ? "cursor-not-allowed border-transparent"
                        : "")
                    }
                    value={toDatepickerTime(datetime)}
                    disabled={datetimeSelectionDisabled || undefined}
                    min={toDatepickerTime(minDatetime)}
                    max={toDatepickerTime(new Date())}
                    step="1"
                    onChange={(e) => {
                      if (e.target.value && e.target.value !== "") {
                        // datetime-local inputs *almost* give us an ISO8601 timestamp-
                        // they're just missing a time zone.
                        setDatetime(new Date(e.target.value + "Z"));
                      } else {
                        setDatetime(null);
                      }
                    }}
                  />{" "}
                  <strong>UTC</strong>
                  <div
                    className={
                      "text-sm py-1 " +
                      (datetime && !datetimeValid ? "text-red-600" : "")
                    }
                  >
                    {datetimeTooEarly ? (
                      <>Select a time after {formatLocale(minDatetime)}.</>
                    ) : datetimeTooLate ? (
                      <>Select a time in the past.</>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>

                <div className="pt-1" />
                <div className="flex flex-row space-x-1 items-baseline">
                  <div className="text-sm">Quick select:</div>
                  <TimeShortcutButton
                    label="Now"
                    getDate={() => truncate(new Date())}
                    onClick={setDatetime}
                    disabled={datetimeSelectionDisabled}
                    minimumTime={minDatetime}
                  />
                  <TimeShortcutButton
                    label="1 hour ago"
                    getDate={() => {
                      const d = new Date();
                      d.setHours(d.getHours() - 1);
                      return truncate(d);
                    }}
                    onClick={setDatetime}
                    disabled={datetimeSelectionDisabled}
                    minimumTime={minDatetime}
                  />
                  <TimeShortcutButton
                    label="24 hours ago"
                    getDate={() => {
                      const d = new Date();
                      d.setHours(d.getHours() - 24);
                      return truncate(d);
                    }}
                    onClick={setDatetime}
                    disabled={datetimeSelectionDisabled}
                    minimumTime={minDatetime}
                  />
                  {lastRollbackTime && (
                    <TimeShortcutButton
                      label="Before last rollback"
                      getDate={() => lastRollbackTime}
                      onClick={setDatetime}
                      disabled={datetimeSelectionDisabled}
                      minimumTime={minDatetime}
                    />
                  )}
                </div>
              </div>

              <div className="pt-6" />
              {envToCopy && datetime && inputsValid && (
                <div className="flex space-x-2">
                  <Button
                    medium
                    disabled={atEnvLimit || !createEnvironmentAction}
                    onClick={() => {
                      setError(null);
                      setCopyInPlaceModalOpen(true);
                    }}
                  >
                    Copy to new environment <>&rarr;</>
                  </Button>

                  <Button
                    medium
                    onClick={() => {
                      setError(null);
                      setRollbackModalOpen(true);
                    }}
                    disabled={!updateEnvironmentAction}
                  >
                    Rollback in place <>&rarr;</>
                  </Button>
                </div>
              )}
            </fieldset>
          </form>
        )}
        {envToCopy && datetime && (
          <>
            <CopyInPlaceModal
              envToCopy={envToCopy}
              datetime={datetime}
              isOpen={copyInPlaceModalOpen}
              onClose={() => setCopyInPlaceModalOpen(false)}
              setError={setError}
            />
            <RollbackModal
              envToCopy={envToCopy}
              datetime={datetime}
              isOpen={rollbackModalOpen}
              onClose={() => setRollbackModalOpen(false)}
              setError={setError}
            />
          </>
        )}
      </ProductionOnlySettings>
    </div>
  );
};

const CopyInPlaceModal: React.FC<{
  isOpen: boolean;
  envToCopy: EnvData;
  datetime: Date;
  onClose: () => void;
  setError: (err: string) => void;
}> = ({ isOpen, envToCopy, datetime, onClose, setError }) => {
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const router = useRouter();
  const { refresh: refreshTenant } = useTenant();
  const { setSelectedEnvName } = useEnv();

  const submit = useCallback(async () => {
    setLoading(true);

    try {
      const r = await fetch(`/web/environment/${envToCopy.pub_id}/recovery`, {
        method: "POST",
        body: JSON.stringify({
          recover_in_place: false,
          fork_at_time: datetime.toISOString(),
        }),
      });
      setLoading(false);

      if (!r.ok) {
        if (r.status === 504) {
          throw "Please contact us to finish the recovery process.";
        } else {
          throw "Something went wrong. Please try again.";
        }
      }
      const newEnv: EnvData = await r.json();
      setSuccess(true);
      await refreshTenant();
      setSelectedEnvName(newEnv.name);
      onClose();
      router.push("/");
    } catch (e: any) {
      setError(e.toString());
      onClose();
    }
  }, [
    envToCopy,
    datetime,
    refreshTenant,
    router,
    setSelectedEnvName,
    onClose,
    setError,
  ]);

  return (
    <Modal isOpen={isOpen} onClose={close} ignoreOutsideClick={true}>
      <div>
        You are recovering <strong>{envToCopy.name}</strong> using a backup from{" "}
        <strong>{formatLocale(datetime)}.</strong>
      </div>
      <div className="pt-2" />
      <div>
        The facts and policy from this backup will be copied into a new
        environment.
      </div>

      <div className="pt-4" />
      <div className="flex space-x-2 items-center">
        <Button
          medium
          theme={success ? "success" : "primary"}
          loading={loading}
          disabled={loading}
          onClick={submit}
        >
          {success ? "Success" : "Copy to new environment"}
        </Button>
        <Button medium theme="default" disabled={loading} onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};

const RollbackModal: React.FC<{
  isOpen: boolean;
  envToCopy: EnvData;
  datetime: Date;
  onClose: () => void;
  setError: (err: string) => void;
}> = ({ isOpen, envToCopy, datetime, onClose, setError }) => {
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmationInput, setConfirmationInput] = useState<string>("");

  const router = useRouter();
  const { refresh: refreshTenant } = useTenant();
  const { setSelectedEnvName } = useEnv();

  const submit = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`/web/environment/${envToCopy.pub_id}/recovery`, {
        method: "POST",
        body: JSON.stringify({
          recover_in_place: true,
          fork_at_time: datetime.toISOString(),
        }),
      });
      setLoading(false);

      if (!r.ok) {
        if (r.status === 504) {
          throw "Please contact us to finish the recovery process.";
        } else {
          throw "Something went wrong. Please try again.";
        }
      }
      setSuccess(true);
      await refreshTenant();
      setSelectedEnvName(envToCopy.name);
      onClose();
      router.push("/");
    } catch (e: any) {
      setError(e.toString());
      onClose();
    }
  }, [
    setLoading,
    setError,
    envToCopy,
    datetime,
    setSuccess,
    refreshTenant,
    router,
    setSelectedEnvName,
    onClose,
  ]);

  return (
    <Modal isOpen={isOpen} onClose={close} ignoreOutsideClick={true}>
      <div>
        You are rolling back <strong>{envToCopy.name}</strong> using a backup
        from <strong className="inline-block">{formatLocale(datetime)}</strong>.
      </div>

      <div className="pt-2" />

      <div>
        <HiOutlineExclamationCircle className="inline mr-2" />
        The facts and policy from this backup will overwrite{" "}
        <strong>{envToCopy.name}</strong>. Any facts and policy changes made
        after <strong>{formatLocale(datetime)}</strong> will be rolled back.
        {/* TODO would be great to show a summary of the changes */}
      </div>

      <div className="pt-2" />

      <div>
        To continue, type the name of the environment you{"'"}re rolling back,{" "}
        <strong>{envToCopy.name}</strong>, into the box below:
      </div>

      <div className="pt-2" />
      <input
        className="border py-1 px-2 text-base rounded"
        value={confirmationInput}
        onChange={(e) => setConfirmationInput(e.target.value)}
        placeholder={envToCopy.name}
      ></input>

      <div className="pt-4" />
      <div className="flex space-x-2 items-center">
        <Button
          medium
          theme={success ? "success" : "primary"}
          loading={loading}
          disabled={loading || confirmationInput !== envToCopy.name}
          onClick={submit}
        >
          {success ? "Success" : "Rollback in place"}
        </Button>
        <Button medium theme="default" disabled={loading} onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};

const TimeShortcutButton: React.FC<{
  label: string;
  minimumTime: Date | null;
  disabled: boolean;
  getDate: () => Date;
  onClick: (d: Date) => void;
}> = ({ label, minimumTime, disabled, getDate, onClick }) => {
  // "Now" is always valid
  const isValid = label === "Now" || (minimumTime && getDate() >= minimumTime);
  return (
    <Button
      small
      disabled={disabled || !isValid}
      onClick={(e) => {
        e.preventDefault();
        onClick(getDate());
      }}
    >
      {label}
    </Button>
  );
};

export default RecoveryManagementView;
